<template lang='pug'>
  v-dialog(v-model='showDialog', max-width='700px' persistent)
    v-card(v-if='notification')
      v-form(ref='form' lazy-validation)
        v-card-title
          h3.text-h5.font-weight-light {{addOrEdit}} Notification
        v-card-text
          v-select(v-model='notification.type' :items='types' label='Type' required :rules='typeRules')
          select-suites(v-model='notification.testSuites' :suites='availableSuites' :key='allSelected') // the :key here will re-render the available suites on change of allSelected which has great effects
          v-combobox(ref='trigger' v-model='notification.trigger' :items='triggers' label='Notify On' multiple chips required :rules='triggerRules')
            template(v-slot:selection='{item, attrs, index, selected, disabled, parent}')
              v-chip.v-chip--select-multi(close v-bind='attrs' :input-value='selected' :disabled='disabled' :key='index' @click:close='parent.selectItem(item)') {{item}}

          // Email
          v-text-field(v-if='notification.type === "Email"' v-model='notification.target' label='Notification Emails' placeholder='example1@example.com, example2@example.com, ...' :rules='emailRules')

          // Slack
          slack-channel(v-if='notification.type === "Slack"' v-model='notification.target')

          // Webhook
          webhook-url(v-if='notification.type === "Webhook"' v-model='notification.target')

          // Text
          sms-phone-numbers(v-if='notification.type === "Text" && hasSms' v-model='notification.target')
          // Not built yet
          div(v-if='beta')
            .text-subtitle-1.font-weight-light
              | Notifications via <strong>{{notification.type}}</strong> is currently a beta-access feature. If you would like this enabled for your account please email us at &nbsp;
              a(href='mailto:support@bionicmetrics.com') support@bionicmetrics.com
        v-card-actions
          v-spacer
          v-btn(text @click='close()') Cancel
          v-btn(color='primary' @click='set' text :disabled='beta') Save
</template>

<script>
import {mapGetters} from 'vuex';
import {isEmpty, get} from 'lodash';
import SlackChannel from '@/components/SlackChannel';
import WebhookUrl from '@/components/WebhookUrl';
import SelectSuites from '@/components/SelectSuites';
import SmsPhoneNumbers from '@/components/SmsPhoneNumbers';

export default {
  name: 'BionicMetricsEditNotification',
  components: {SmsPhoneNumbers, SelectSuites, WebhookUrl, SlackChannel},
  props: {
    dashboard: {
      type: Object
    }
  },
  computed: {
    hasSms() {
      return get(this.account, 'entitlements.hasSms') || get(this.user, 'superAdmin');
    },
    beta() {
      return this.notification.type === 'Text' && !this.hasSms;
    },
    allSelected() {
      if (!this.notification || !this.notification.testSuites) return false;
      for (const ts of this.notification.testSuites) {
        if (ts.guid === 'all') return true;
      }
      return false;
    },
    availableSuites() {
      if (!this.dashboard || !this.dashboard.testSuites) return [];
      const avail = this.dashboard.testSuites.map((ts) => ({
        name: ts.name,
        description: ts.description,
        guid: ts.guid,
        testSuite: ts._id,
        disabled: this.allSelected
      }));
      return [{name: 'All', description: 'Notify for all suites', guid: 'all'}].concat(avail);
    },
    ...mapGetters({
      user: 'getUser',
      account: 'getAccount'
    })
  },
  methods: {
    close() {
      this.showDialog = false;
      this.isNew = false;
      this.notification = null;
      this.addOrEdit = 'Edit';
      this.editingIndex = null;
    },
    edit(notification, index) {
      this.isNew = false;
      this.notification = notification;
      this.addOrEdit = 'Edit';
      this.showDialog = true;
      this.editingIndex = index;
    },
    add() {
      this.isNew = true;
      // So you can click away and come back if you want
      if (!this.notification) {
        this.notification = {
          trigger: [],
          testSuites: [],
          type: null,
          target: null
        };
      }

      this.addOrEdit = 'Add';
      this.showDialog = true;
    },
    set() {
      if (!this.$refs.form.validate()) return;
      if (!this.notification.testSuites || !this.notification.testSuites.length) {
        this.notification.testSuites = [{guid: 'all', name: 'All'}];
      }
      if (this.isNew) this.$emit('add', this.notification);
      else this.$emit('edit', this.notification, this.editingIndex);
      this.close();
    },
    closeOnSelect(val) {
      if (val.length) this.$refs.trigger.blur();
    },
    emailsValid(csv) {
      if (this.notification.type !== 'Email') return true;
      if (!csv || csv === '') return 'Notification emails required';
      // eslint-disable-next-line
      const regex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const emails = csv.replace(/\s/g, '').split(',');
      for (let i = 0; i < emails.length; i++) {
        if (emails[i] === '' || !regex.test(emails[i])) {
          return `"${emails[i]}" is an invalid email`;
        }
      }
      return true;
    }
  },
  mounted() {
  },
  data() {
    return {
      addOrEdit: 'Edit',
      showDialog: false,
      notification: null,
      isNew: false,
      editingIndex: null,
      triggers: [
        'Start',
        'Pass',
        'Fail',
        'Fault'
      ],
      types: [
        'Email',
        'Text',
        'Slack',
        'Webhook'
      ],
      required: [(v) => !!v || 'Required field'],
      triggerRules: [
        (v) => !isEmpty(v) || 'Notification event required'
      ],
      typeRules: [
        (v) => !!v || 'Notification type required'
      ],
      emailRules: [
        (v) => this.emailsValid(v)
      ]
    };
  }
};
</script>

<!-- Add 'scoped' attribute to limit CSS to this component only -->
<style scoped>
</style>
